import type { Level, MapId } from "@69ly/maps";
import { useMemo, useSyncExternalStore } from "react";
import { useCallouts } from "../lib/callouts.ts";
import { AtPoint } from "./Radar.tsx";

// Shown or hidden for every map at once, and remembered in this browser.
const KEY = "69ly:callouts";
const listeners = new Set<() => void>();
let shown = (() => {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
})();

export function setCalloutsShown(value: boolean) {
  shown = value;
  try {
    localStorage.setItem(KEY, value ? "on" : "off");
  } catch {
    // Private windows: just for this visit.
  }
  listeners.forEach((l) => l());
}

export function useCalloutsShown(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => shown,
  );
}

/** About how wide a label is on screen, in pixels: 11 px bold text. */
const labelWidth = (name: string) => name.length * 6.4 + 8;
const LABEL_HEIGHT = 15;

/**
 * Callout names on the radar. Where two would overlap at this zoom, the bigger
 * callout keeps its label; zooming in brings the others back.
 */
export function CalloutLayer({ map, level, scale, width }: { map: MapId; level: Level; scale: number; width: number }) {
  const { labels: labelsOf } = useCallouts();
  const labels = useMemo(() => {
    const all = [...labelsOf(map, level)].sort((a, b) => b.size - a.size);
    const px = (width || 640) * scale;
    // A thumbnail-sized map has no room for names.
    if (px < 420) return [];
    const placed: { x0: number; y0: number; x1: number; y1: number }[] = [];
    return all.filter((l) => {
      const w = labelWidth(l.name);
      const box = { x0: l.u * px - w / 2, x1: l.u * px + w / 2, y0: l.v * px - LABEL_HEIGHT / 2, y1: l.v * px + LABEL_HEIGHT / 2 };
      if (placed.some((p) => box.x0 < p.x1 && box.x1 > p.x0 && box.y0 < p.y1 && box.y1 > p.y0)) return false;
      placed.push(box);
      return true;
    });
  }, [map, level, scale, width, labelsOf]);
  return (
    <div className="callouts" aria-hidden="true">
      {labels.map((l) => (
        <AtPoint key={`${l.name}-${l.u}-${l.v}`} u={l.u} v={l.v} className="callout-spot">
          <span className="callout">{l.name}</span>
        </AtPoint>
      ))}
    </div>
  );
}
